import { useState, useRef } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { getStudentAttendanceHours } from "../../lib/api";
import { printRef } from "../../lib/print";

export default function StudentHoursTab({ startDate, endDate, setStartDate, setEndDate }) {
  const [studentHoursReport, setStudentHoursReport] = useState(null);
  const [loadingDirectorReport, setLoadingDirectorReport] = useState(false);
  const studentHoursRef = useRef(null);

  const fetchStudentHoursReport = async () => {
    setLoadingDirectorReport(true);
    try {
      const res = await getStudentAttendanceHours(startDate, endDate);
      setStudentHoursReport(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load student hours report"); }
    finally { setLoadingDirectorReport(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle>Student Attendance Hours</CardTitle><CardDescription>Per-student attendance breakdown with days and hours</CardDescription></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
          <div className="space-y-2"><Label>End Date</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
          <div className="flex items-end gap-2">
            <Button onClick={fetchStudentHoursReport} disabled={loadingDirectorReport} data-testid="fetch-student-hours">{loadingDirectorReport ? "Loading..." : "Generate Report"}</Button>
            {studentHoursReport && <Button variant="outline" onClick={() => printRef("Student Attendance Hours", studentHoursRef)}>Print</Button>}
          </div>
        </div>
        {studentHoursReport && (
          <div ref={studentHoursRef} className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg"><div className="text-2xl font-bold text-blue-700">{studentHoursReport.summary.total_students}</div><div className="text-sm text-gray-600">Total Students</div></div>
              <div className="p-4 bg-green-50 rounded-lg"><div className="text-2xl font-bold text-green-700">{studentHoursReport.expected_school_days}</div><div className="text-sm text-gray-600">School Days</div></div>
              <div className="p-4 bg-purple-50 rounded-lg"><div className="text-2xl font-bold text-purple-700">{studentHoursReport.students?.length > 0 ? (studentHoursReport.students.reduce((sum, s) => sum + (s.expected_hours || 0), 0) / studentHoursReport.students.length / Math.max(studentHoursReport.expected_school_days, 1)).toFixed(1) + 'h' : '—'}</div><div className="text-sm text-gray-600">Avg Hours/Day</div></div>
              <div className="p-4 bg-orange-50 rounded-lg"><div className="text-2xl font-bold text-orange-700">{studentHoursReport.summary.average_attendance_all}%</div><div className="text-sm text-gray-600">Avg Attendance</div></div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead><tr className="bg-[#00305E] text-white"><th className="border p-2 text-left">Student Name</th><th className="border p-2 text-center">Days Attended</th><th className="border p-2 text-center">Expected Days</th><th className="border p-2 text-center">Total Hours</th><th className="border p-2 text-center">Expected Hours</th><th className="border p-2 text-center">Attendance %</th></tr></thead>
                <tbody>
                  {studentHoursReport.students.map((student, idx) => (
                    <tr key={student.student_id} className={student.attendance_pct < 50 ? "bg-pink-100" : idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="border p-2 font-medium">{student.student_name}</td><td className="border p-2 text-center">{student.days_attended}</td><td className="border p-2 text-center">{student.expected_days}</td><td className="border p-2 text-center">{student.total_hours}</td><td className="border p-2 text-center">{student.expected_hours}</td>
                      <td className={`border p-2 text-center font-bold ${student.attendance_pct < 50 ? "text-red-600" : student.attendance_pct >= 90 ? "text-green-600" : ""}`}>{student.attendance_pct}%</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="bg-green-100 font-bold"><td className="border p-2">Average - All Students</td><td className="border p-2 text-center" colSpan="4"></td><td className="border p-2 text-center">{studentHoursReport.summary.average_attendance_all}%</td></tr></tfoot>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
